import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import ToggleField from '../../../FormLegacy/FormFieldsV2/ToggleField'
import API from '../../../../api/api'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'

const PermissionAccessBlock = ({
  isRecurring,
  resident,
  keyAccess,
  keylessAccess,
  customAccess,
  pinCode,
  setFieldValue,
}) => {
  const { t } = useTranslation(['visitors'])
  const [isUserConnectedToSaltoSpace, setIsUserConnectedToSaltoSpace] = useState(false)
  const [isUserConnectedToRemoteLock, setIsUserConnectedToRemoteLock] = useState(false)
  const building = useSelector(selectActiveBuilding)
  const brivoAvailable = building?.brivoAvailable
  const brivoAvailableVisitor = building?.brivoVisitorAccessAvailable
  const swiftlaneAvailable = building?.swiftlaneAvailable
  const swiftlaneVisitorAccessAvailable = building?.swiftlaneVisitorAccessAvailable
  const butterflyVisitorAccessAvailable = building?.butterflyVisitorAccessAvailable
  const saltoSpaceAvailable = building?.saltoSpaceAvailable
  const saltoSpacePinVisitorAccessAvailable = building?.saltoSpacePinVisitorAccessAvailable
  const showPinCodeAccess =
    (swiftlaneAvailable && swiftlaneVisitorAccessAvailable) ||
    (brivoAvailable && brivoAvailableVisitor) ||
    butterflyVisitorAccessAvailable ||
    (saltoSpaceAvailable && saltoSpacePinVisitorAccessAvailable)

  const physicalKeyAvailable = building?.physicalKeyAvailable
  const customAccessAvailable = building?.customAccessAvailable
  const iglooWorkAvailable = building?.iglooWorkAvailable
  const iglooWorkVisitorAccessAvailable = building?.iglooWorkVisitorAccessAvailable
  const showIglooAccess = iglooWorkAvailable && iglooWorkVisitorAccessAvailable

  const checkIfUserIsConnectedToSaltoSpace = async () => {
    try {
      let email = resident?.user?.email ?? resident?.email

      if (!email && resident?.value) {
        const { data } = await API.getClientById(resident.value)
        email = data?.user?.email
      }

      if (!email) {
        setIsUserConnectedToSaltoSpace(false)
        return
      }

      const { data } = await API.isSaltoSpaceUserConnected(email)
      setIsUserConnectedToSaltoSpace(data)
    } catch {
      setIsUserConnectedToSaltoSpace(false)
    }
  }

  const checkIfUserIsConnectedToRemoteLock = async () => {
    if (!building?.remoteLockAvailable || !building?.remoteLockVisitorAccessAvailable) return
    try {
      const residentId = resident?.id ?? resident?.value
      const { data } = await API.getRemoteLockUserDevices(residentId)
      setIsUserConnectedToRemoteLock(!!data?.remoteLockId)
    } catch {
      setIsUserConnectedToRemoteLock(false)
    }
  }

  useEffect(() => {
    if (resident) {
      checkIfUserIsConnectedToSaltoSpace()
      checkIfUserIsConnectedToRemoteLock()
    }
  }, [resident])

  const showKeylessViaConnection =
    isRecurring &&
    resident &&
    (resident.id || resident.value) &&
    (isUserConnectedToSaltoSpace || isUserConnectedToRemoteLock)
  const showKeyless = showKeylessViaConnection || showIglooAccess

  const hasAnyAccess =
    physicalKeyAvailable ||
    customAccessAvailable ||
    showKeyless ||
    showPinCodeAccess ||
    showIglooAccess

  if (!resident || !hasAnyAccess) {
    return null
  }

  return (
    <div className="columns">
      <div className="column is-12">
        {isRecurring && (
          <p className="permission-title">{t('visitors:permissionAccess.accessType')}</p>
        )}
        {physicalKeyAvailable && (
          <div className="field">
            <ToggleField
              name="keylessAccess"
              value={keylessAccess}
              onChange={() => setFieldValue('keylessAccess', !keylessAccess)}
              labelOnRight
              label={t('visitors:permissionAccess.physicalKeyRelease')}
            />
            <div className="permission-description">
              {isRecurring
                ? t('visitors:permissionAccess.physicalKeyDescriptionRecurring')
                : t('visitors:permissionAccess.physicalKeyDescriptionActive')}
            </div>
          </div>
        )}
        {customAccessAvailable && (
          <div className="field">
            <ToggleField
              name="customAccess"
              value={customAccess}
              onChange={() => setFieldValue('customAccess', !customAccess)}
              labelOnRight
              label={`${building.customAccessName}`}
            />
          </div>
        )}
        {showKeyless && (
          <div className="field">
            <ToggleField
              name="keyAccess"
              value={keyAccess}
              onChange={() => setFieldValue('keyAccess', !keyAccess)}
              labelOnRight
              label={t('visitors:permissionAccess.keylessAccess')}
            />
            <div className="permission-description">
              {t('visitors:permissionAccess.keylessAccessDescription')}
            </div>
          </div>
        )}
        {showPinCodeAccess && (
          <div className="field">
            <ToggleField
              name="pinCode"
              value={pinCode}
              onChange={() => setFieldValue('pinCode', !pinCode)}
              labelOnRight
              label={t('visitors:permissionAccess.pinCodeAccess')}
            />
            <div className="permission-description">
              {t('visitors:permissionAccess.pinCodeDescription')}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

PermissionAccessBlock.propTypes = {
  customAccess: PropTypes.bool,
  isRecurring: PropTypes.bool.isRequired,
  keyAccess: PropTypes.bool.isRequired,
  keylessAccess: PropTypes.bool.isRequired,
  pinCode: PropTypes.bool,
  resident: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
}

export default PermissionAccessBlock
